import { hotkeyHint } from "@/hooks/useCoverageBetaHotkeys";
import {
  BRIGHTNESS_MAX,
  BRIGHTNESS_MIN,
  BrightnessView,
  useCoverageBetaStore,
} from "@/store/useCoverageBetaStore";
import {
  ActionIcon,
  Button,
  Popover,
  Slider,
  Text,
  Tooltip,
  type FloatingPosition,
} from "@mantine/core";
import { IconSun } from "@tabler/icons-react";
import { useState } from "react";

const ICON_SIZE = 14;

/**
 * Brightness control for one image view: a toolbar button that opens a slider
 * popup, plus a live % readout and Reset. The button fills in while brightness
 * is off 100%. The same F/G/H hotkeys adjust whichever view the mouse is over,
 * so both views expose identical affordances (slider + hotkeys).
 */
export const BrightnessControl = ({
  view,
  position = "bottom",
}: {
  view: BrightnessView;
  position?: FloatingPosition;
}) => {
  const brightness = useCoverageBetaStore((s) => s.brightnessByView[view]);
  const setBrightness = useCoverageBetaStore((s) => s.setBrightness);
  const resetBrightness = useCoverageBetaStore((s) => s.resetBrightness);
  const recordInteraction = useCoverageBetaStore((s) => s.recordInteraction);
  const [opened, setOpened] = useState(false);
  const isModified = brightness !== 100;

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position={position}
      withArrow
      shadow="md"
      trapFocus={false}
    >
      <Popover.Target>
        <Tooltip
          label={`Brightness (${hotkeyHint("brightness_up")} / ${hotkeyHint(
            "brightness_down",
          )} / ${hotkeyHint("brightness_reset")})`}
          position={position}
        >
          <ActionIcon
            size="sm"
            variant={isModified ? "filled" : "light"}
            color="gray"
            onClick={() => setOpened((o) => !o)}
          >
            <IconSun size={ICON_SIZE} />
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <div style={{ width: 180 }}>
          <div className="flex items-center justify-between mb-2">
            <Text size="xs" fw={600}>
              Brightness
            </Text>
            <Text size="xs" c="dimmed">
              {brightness}%
            </Text>
          </div>
          <Slider
            value={brightness}
            onChange={(v) => setBrightness(view, v)}
            min={BRIGHTNESS_MIN}
            max={BRIGHTNESS_MAX}
            step={5}
            size="sm"
            label={(v) => `${v}%`}
          />
          <div className="flex justify-end mt-2">
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              disabled={!isModified}
              onClick={() => {
                resetBrightness(view);
                recordInteraction("brightness_reset", "click");
              }}
            >
              Reset
            </Button>
          </div>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
};
